import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Query } from '../../_common';
import './MileageConfirmation.scss';
import { DELETE_JOB_IMAGE, UPLOAD_IMAGE_TO_JOB } from '../../../graphql/client/mutations';
import {
    GetJobImages,
    GetJobImages_getJobImages as JobImagesList,
    UploadImageToJob,
    UploadImageToJobInput,
} from '../../../types/schemaTypes';
import { GET_JOB_IMAGES } from '../../../graphql/client/queries';
import { Error, Loading } from '../../_common';
import MileageConfirmationImageList from './MileageConfirmationImageList';
import { ToastContainer, toast } from 'react-toastify';
import { toastOpt } from '../../../utils/helpers/toastHelper'
import { Alert, Col } from 'react-bootstrap';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';

interface MileageConfirmationProps {
    jobId: number;
}

const ErrorMessages = {
    delete: 'Failed to delete image',
};

const UploadErrorMessages = {
    upload: 'Failed to upload image.',
};

const Messages = {
    delete: 'Image deleted successfully',
};

const SuccessMessage = {
    upload: 'Image uploaded successfully',
};

const MileageConfirmation = ({ jobId }: MileageConfirmationProps): React.ReactElement | null => {
    const [mileageImg, setmileageImg] = useState<any>('');
    const canEditJobs = useAuthContext().userHasPermission(PermissionEnum.EditJob);

    const handleLoadFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (!event || !event.target || !event.target.files) return;
        const file = event.target.files[0];
        setmileageImg(file);
        event.target.value = '';
    };

    const onCompleted = action => {
        toast.success(Messages[action], toastOpt);
    };

    const onUploadError = action => {
        toast.error(UploadErrorMessages[action], toastOpt);
    };

    const onActionError = action => {
        toast.error(ErrorMessages[action], toastOpt);
    };

    const onUploadCompleted = action => {
        toast.success(SuccessMessage[action], toastOpt);
    };

    const [uploadImageMutation, { loading: mutationLoading }] = useMutation<UploadImageToJob>(UPLOAD_IMAGE_TO_JOB, {
        onCompleted: (data) => {
            const updateImageErrors = data.uploadImageToJob?.errors ?? [];
            if (updateImageErrors.length > 0) {
                onUploadError('upload');
            } else {
                onUploadCompleted('upload');
            }
        },
        onError: () => onUploadError('upload'),
        refetchQueries: () => {
            setmileageImg('');
            return [
                {
                    query: GET_JOB_IMAGES,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    const [deletejobImage] = useMutation(DELETE_JOB_IMAGE, {
        onCompleted: () => onCompleted('delete'),
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_JOB_IMAGES,
                    variables: { jobId: jobId },
                },
            ];
        },
    });

    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { id: record.id } };
        await deletejobImage({ variables });
    }, []);

    const fileUpload = async (): Promise<void> => {
        const reader = new FileReader();
        const file = mileageImg;
        reader.onloadend = async () => {
            const imageName = `Job-${jobId}-${file.name}`;
            const imageData = reader.result ? reader.result.toString().replace(/^data:(.*,)?/, '') : '';
            const extension = file.name.split('.').pop();

            const uploadImageToLoadInput: UploadImageToJobInput = {
                jobId: jobId,
                uploadImageToJob: {
                    imageName: imageName,
                    imageData: imageData,
                    name: extension.toUpperCase(),
                },
            };
            uploadImageMutation({
                variables: { input: uploadImageToLoadInput },
            });
        };

        reader.readAsDataURL(file);
    };

    const uploadButtonDisabled = (): boolean => {
        if (!mileageImg) return true;
        if (!canEditJobs) return true;
        if (mutationLoading) return true;
        if (mileageImg.name.length > 0) {
            return false;
        }
        return true;
    };

    return (
        <Query<GetJobImages>
            query={GET_JOB_IMAGES}
            variables={{
                jobId: jobId,
            }}
        >
            {({ error, data, loading, refetch }): React.ReactElement | null => {
                if (loading) {
                    return <Loading />;
                }
                if (error) {
                    return <Error error={error} />;
                }

                const images: JobImagesList[] = [];
                const imagesList = data?.getJobImages ?? [];
                if (imagesList.length > 0) {
                    (imagesList as (JobImagesList | null)[]).forEach((element: JobImagesList | null): void => {
                        if (element != null) {
                            images.push(element);
                        }
                    });
                }

                return (
                    <div>
                        <ToastContainer />
                        {mutationLoading ? (
                            <div className="loading-icon">
                                <span>
                                    <Loading />
                                </span>
                            </div>
                        ) : (
                            ''
                        )}
                        <div className="Row MileageConfirmation-FileBrowser-Background">
                            <label className="MileageConfirmation-FileBrowser-Label">
                                {mileageImg ? mileageImg.name : 'Click here to choose file'}
                                <input
                                    type="file"
                                    onChange={event => handleLoadFileChange(event)}
                                    className="MileageConfirmation-FileBrowser-Input"
                                />
                            </label>
                            <button
                                disabled={uploadButtonDisabled()}
                                onClick={fileUpload}
                                className={`MileageConfirmation-Upload-Button`}
                            >
                                Upload
                            </button>
                        </div>
                        <br></br>
                        <div className="ImageList-Container">
                            <div className="Images-Bottom-Container">
                                <Col md={4}>
                                    <div className="ImageList-Header-Title">Attachment</div>
                                </Col>
                                <div className="ImageList-Header-Title">Received Date</div>
                                <div className="ImageList-Header-Title">Uploaded By</div>
                                <div className="ImageList-Header-Title">Action</div>
                            </div>
                            {images.length > 0 && (
                                <MileageConfirmationImageList
                                    images={images}
                                    refetch={refetch}
                                    canEditJobs={canEditJobs}
                                    isLoading={loading}
                                    onDelete={onDelete}
                                />
                            )}
                        </div>
                    </div>
                );
            }}
        </Query>
    );
};

export default MileageConfirmation;
